import { useState, useMemo } from "react";

/**
 * Tiny inline sparkline for hive cards with hover-to-reveal tooltip.
 *
 * Props:
 *   values   : number[]  — chart data, oldest → newest
 *   labels   : string[]  — same length as values, used by the tooltip (e.g. "Mon", "Jul")
 *   colors   : string[]  — per-point dot colour. Optional. Falls back to neutral grey.
 *   unit     : string    — e.g. "°F", "lb" — rendered after the number in the tooltip
 *   width / height : layout dimensions in px
 *   ariaLabel : screen-reader label
 *   showAxisLine : if true, draws a faint zero-baseline line
 */
const DEFAULT_COLOR = "#a8a29e";

export default function TempSparkline({
  values = [],
  labels = [],
  colors = [],
  unit = "",
  width = 70,
  height = 22,
  ariaLabel,
  showAxisLine = false,
  onPointClick,
}) {
  const [hover, setHover] = useState(null); // index of hovered point | null

  const { points, pad, min, max, span, pathD } = useMemo(() => {
    if (!values.length) return { points: [], pad: 3, min: 0, max: 0, span: 1, pathD: "" };
    const pad = 3;
    const innerW = width - pad * 2;
    const innerH = height - pad * 2;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = Math.max(1, max - min);
    const stepX = values.length > 1 ? innerW / (values.length - 1) : 0;
    const pts = values.map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + innerH - ((v - min) / span) * innerH;
      return [x, y];
    });
    const d = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    return { points: pts, pad, min, max, span, pathD: d };
  }, [values, width, height]);

  if (!values.length) return null;

  const hoveredLabel = hover != null ? (labels[hover] ?? `#${hover + 1}`) : null;
  const hoveredValue = hover != null ? values[hover] : null;
  // tooltip position — clamp inside the svg so it doesn't overflow the card edge
  const tipX = hover != null ? Math.max(28, Math.min(width - 28, points[hover][0])) : 0;

  // Hit-test: convert pointer x to nearest data index
  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const innerW = width - pad * 2;
    const stepX = values.length > 1 ? innerW / (values.length - 1) : 0;
    if (stepX === 0) { setHover(0); return; }
    const i = Math.round((localX - pad) / stepX);
    if (i >= 0 && i < values.length) setHover(i);
  };

  return (
    <span className="relative inline-block" style={{ width, height: height + (hover != null ? 18 : 0) }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel || `Trend of ${values.length} points`}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
        onTouchStart={(e) => { handleMove(e.touches[0]); }}
        onTouchMove={(e) => { handleMove(e.touches[0]); }}
        onTouchEnd={() => setHover(null)}
        onClick={(e) => {
          if (!onPointClick) return;
          // Use the most recently hovered index, else compute from click x
          let idx = hover;
          if (idx == null) {
            const rect = e.currentTarget.getBoundingClientRect();
            const localX = e.clientX - rect.left;
            const innerW = width - pad * 2;
            const stepX = values.length > 1 ? innerW / (values.length - 1) : 0;
            idx = stepX === 0 ? 0 : Math.max(0, Math.min(values.length - 1, Math.round((localX - pad) / stepX)));
          }
          e.preventDefault();
          e.stopPropagation();
          onPointClick(idx, values[idx], labels[idx]);
        }}
        data-testid="temp-sparkline"
        style={{ touchAction: "none", cursor: onPointClick ? "pointer" : "crosshair" }}
      >
        {showAxisLine && (
          <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad}
                stroke="#e7e5e4" strokeWidth="0.5" strokeDasharray="1.5 2" />
        )}
        <path d={pathD} fill="none" stroke="#78716c" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        {points.map(([x, y], i) => {
          const isHover = hover === i;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={isHover ? 2.8 : (i === 0 ? 2.2 : 1.6)}
              fill={colors[i] || DEFAULT_COLOR}
              stroke="#fff"
              strokeWidth="0.75"
            />
          );
        })}
        {hover != null && (
          <line x1={points[hover][0]} y1={pad} x2={points[hover][0]} y2={height - pad}
                stroke="#a8a29e" strokeWidth="0.5" strokeDasharray="1.5 1.5" />
        )}
      </svg>
      {hover != null && (
        <span
          className="absolute z-20 pointer-events-none whitespace-nowrap rounded-md bg-stone-900 text-white text-[10px] font-medium px-1.5 py-0.5 shadow-md"
          style={{
            left: tipX,
            top: height + 2,
            transform: "translateX(-50%)",
          }}
        >
          <span className="text-stone-300 mr-1">{hoveredLabel}</span>
          <span className="tabular-nums">
            {Number.isInteger(hoveredValue) ? hoveredValue : hoveredValue.toFixed(1)}{unit && ` ${unit}`}
          </span>
        </span>
      )}
    </span>
  );
}
